import React from 'react';

import { useDispatch, useSelector } from 'react-redux';

import VerifySectionHeader from '@/pages/settings/verify/kyc-sections/VerifySectionHeader';
import KycProofOfAddress from '@/pages/settings/verify/kyc-sections/KycProofOfAddress';
import KycPaymentMethod from '@/pages/settings/verify/kyc-sections/KycPaymentMethod';
import KycSourceOfFunds from '@/pages/settings/verify/kyc-sections/KycSourceOfFunds';
import Spinner from '@/components/UI/Spinner';
import {
  selectKycAccountVerifiedMexico,
  selectKycDocumentsError,
  selectKycDocumentsLatest,
  selectKycDocumentsLoaded,
  selectKycDocumentsLoading,
  selectKycDocumentsRefreshedOn,
} from '@/store/selectors/kycSelectors';
import {
  selectFastRegMxV2Tag,
  selectIsFastReg,
  selectIsFastRegMx,
  selectIsFastRegTh,
} from '@/store/selectors/userSelectors';
import { VerifySectionType } from '@/models/enum/verify-section-type.enum';
import { isEmpty } from '@/shared/utility';
import { KycSowq } from '@/pages/settings/verify/kyc-sections/KycSowq';
import { KycSupportingDocuments } from '@/pages/settings/verify/kyc-sections/KycSupportingDocuments';
import VerifyRefresh from '@/pages/settings/verify/kyc-sections/VerifyRefresh';
import { kycGetDocuments } from '@/store/actions/kycActions';
import { KycPhotoID } from '@/pages/settings/verify/kyc-sections/KycPhotoID';
import Card from '@/components/shared/card/Card';
import UserCompleteProfileFormManager from '@/components/shared/complete-profile/UserCompleteProfileFormManager';

const VerifySection = ({ type }: { type: VerifySectionType }) => {
  switch (type) {
    case VerifySectionType.PhotoId:
      return <KycPhotoID />;
    case VerifySectionType.POA:
      return <KycProofOfAddress />;
    case VerifySectionType.POP:
      return <KycPaymentMethod />;
    case VerifySectionType.SOF:
      return <KycSourceOfFunds />;
    case VerifySectionType.SOWQ:
      return <KycSowq />;
    case VerifySectionType.SupportingDocuments:
      return <KycSupportingDocuments />;
    default:
      return null;
  }
};

const getSectionsOrder = () => {
  return [
    VerifySectionType.PhotoId,
    VerifySectionType.POA,
    VerifySectionType.POP,
    VerifySectionType.SOWQ,
    VerifySectionType.SupportingDocuments,
    VerifySectionType.SOF,
  ];
};

const VerifySections = (): JSX.Element => {
  const userKyc = useSelector(selectKycDocumentsLatest);
  const kycDocumentsLoading = useSelector(selectKycDocumentsLoading);
  const kycError = useSelector(selectKycDocumentsError);
  const kycRefreshedOn = useSelector(selectKycDocumentsRefreshedOn);
  const kycDocumentsLoaded = useSelector(selectKycDocumentsLoaded);
  const accountVerified = useSelector(selectKycAccountVerifiedMexico);
  const isFastReg = useSelector(selectIsFastReg);
  const isFastRegMxTag = useSelector(selectIsFastRegMx);
  const isFastRegMxV2TagPresent = useSelector(selectFastRegMxV2Tag);
  const isFastRegTh = useSelector(selectIsFastRegTh);
  const dispatch = useDispatch();

  const refreshKyc = (): void => {
    dispatch(kycGetDocuments());
  };

  if (!kycDocumentsLoaded && kycDocumentsLoading) {
    return (
      <div className="mx-auto flex min-h-[300px] max-w-[720px] items-center justify-center px-8 py-16">
        <Spinner dark />
      </div>
    );
  }

  // Profile completion
  // Thailand fast reg, Mexico fast reg, Mexico fast reg v2 (without CURP), normal fast reg
  if (isFastReg || isFastRegMxV2TagPresent || isFastRegMxTag || isFastRegTh) {
    return (
      <Card>
        <UserCompleteProfileFormManager />
      </Card>
    );
  }

  return (
    <>
      <VerifySectionHeader
        isAccountVerified={accountVerified}
        kycDocumentsEmpty={isEmpty(userKyc)}
      />
      <div className="mb-2">
        <VerifyRefresh
          loading={kycDocumentsLoading}
          error={kycError}
          lastRefreshedOn={kycRefreshedOn}
          onRefreshClick={refreshKyc}
        />
      </div>
      <div className="grid grid-cols-1 gap-8" id="e2e_verify_grid">
        {getSectionsOrder().map(type => (
          <VerifySection type={type} key={type} />
        ))}
      </div>
    </>
  );
};

export default VerifySections;
